import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import './Comments.css';

const PAGE = 20;

function timeAgo(date) {
    if (!date) return '';
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}

function Author({ author }) {
    if (!author) return <strong className="comment-author">Unknown</strong>;
    return (
        <Link to={`/user/${author.publicId}`} className="comment-author">
            {author.profilePicture ? (
                <img src={author.profilePicture} alt="" className="comment-avatar" />
            ) : (
                <span className="comment-avatar comment-avatar-fallback">
                    {(author.username || '?')[0].toUpperCase()}
                </span>
            )}
            <span>{author.username || 'Unknown'}</span>
        </Link>
    );
}

/** A single comment or reply row, with like / edit / delete and (for top-level) reply controls. */
function CommentItem({ comment, picturePublicId, currentUserPublicId, isReply, onDeleted, onReplyAdded, onReplyDeleted, translations }) {
    const { user } = useAuth();
    const [liked, setLiked] = useState(comment.likedByUser);
    const [likeCount, setLikeCount] = useState(comment.likeCount || 0);
    const [likeBusy, setLikeBusy] = useState(false);

    const [editing, setEditing] = useState(false);
    const [editText, setEditText] = useState(comment.text);
    const [text, setText] = useState(comment.text);
    const [isEdited, setIsEdited] = useState(comment.isEdited);

    const [replyCount, setReplyCount] = useState(comment.replyCount || 0);
    const [showReplies, setShowReplies] = useState(false);
    const [replies, setReplies] = useState([]);
    const [repliesLoaded, setRepliesLoaded] = useState(false);
    const [replyOpen, setReplyOpen] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [replyBusy, setReplyBusy] = useState(false);

    const isOwn = currentUserPublicId && comment.author?.publicId === currentUserPublicId;
    const t = (key, fallback) => translations?.[key] || fallback;

    const handleLike = async () => {
        if (!user || likeBusy) return;
        setLikeBusy(true);
        try {
            const res = await api.likeComment(comment.publicId);
            setLiked(res.likedByUser);
            setLikeCount(res.likeCount);
        } catch {
            // Ignore
        } finally {
            setLikeBusy(false);
        }
    };

    const loadReplies = useCallback(async () => {
        try {
            const data = await api.getReplies(picturePublicId, comment.publicId, 0, PAGE);
            setReplies(data);
            setRepliesLoaded(true);
        } catch {
            // Ignore
        }
    }, [picturePublicId, comment.publicId]);

    const toggleReplies = async () => {
        if (!showReplies && !repliesLoaded) await loadReplies();
        setShowReplies((v) => !v);
    };

    const submitReply = async (e) => {
        e.preventDefault();
        const value = replyText.trim();
        if (!value || replyBusy) return;
        setReplyBusy(true);
        try {
            const created = await api.addComment(picturePublicId, value, comment.publicId);
            setReplies((prev) => [...prev, created]);
            setReplyCount((c) => c + 1);
            setReplyText('');
            setReplyOpen(false);
            setShowReplies(true);
            setRepliesLoaded(true);
            if (onReplyAdded) onReplyAdded();
        } catch {
            // Ignore
        } finally {
            setReplyBusy(false);
        }
    };

    const saveEdit = async (e) => {
        e.preventDefault();
        const value = editText.trim();
        if (!value) return;
        try {
            const updated = await api.editComment(comment.publicId, value);
            setText(updated.text);
            setIsEdited(updated.isEdited);
            setEditing(false);
        } catch {
            // Ignore
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(t('comment-delete-confirm', 'Delete this comment?'))) return;
        try {
            await api.deleteComment(comment.publicId);
            if (onDeleted) onDeleted(comment.publicId);
        } catch {
            // Ignore
        }
    };

    return (
        <div className={`comment ${isReply ? 'comment-reply' : ''}`}>
            <div className="comment-head">
                <Author author={comment.author} />
                <span className="comment-time">{timeAgo(comment.createdAt)}{isEdited ? ' · edited' : ''}</span>
            </div>

            {editing ? (
                <form className="comment-edit" onSubmit={saveEdit}>
                    <textarea value={editText} onChange={(e) => setEditText(e.target.value)} maxLength={1000} rows={2} />
                    <div className="comment-edit-actions">
                        <button type="submit" className="comment-btn primary">{t('comment-save', 'Save')}</button>
                        <button type="button" className="comment-btn secondary" onClick={() => { setEditing(false); setEditText(text); }}>{t('comment-cancel', 'Cancel')}</button>
                    </div>
                </form>
            ) : (
                <p className="comment-text">{text}</p>
            )}

            <div className="comment-actions">
                <button
                    className={`comment-like ${liked ? 'liked' : ''}`}
                    onClick={handleLike}
                    disabled={!user}
                    title={user ? (liked ? 'Unlike' : 'Like') : 'Log in to like'}
                >
                    {liked ? '♥' : '♡'} {likeCount > 0 ? likeCount : ''}
                </button>

                {!isReply && user && (
                    <button className="comment-link" onClick={() => setReplyOpen((v) => !v)}>{t('comment-reply', 'Reply')}</button>
                )}
                {isOwn && !editing && (
                    <>
                        <button className="comment-link" onClick={() => setEditing(true)}>{t('comment-edit', 'Edit')}</button>
                        <button className="comment-link comment-link-danger" onClick={handleDelete}>{t('comment-delete', 'Delete')}</button>
                    </>
                )}
            </div>

            {!isReply && replyOpen && (
                <form className="comment-reply-form" onSubmit={submitReply}>
                    <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder={`${t('comment-reply-to', 'Reply to')} ${comment.author?.username || 'comment'}...`}
                        maxLength={1000}
                        rows={2}
                    />
                    <button type="submit" className="comment-btn primary" disabled={replyBusy || !replyText.trim()}>
                        {replyBusy ? t('comment-posting', 'Posting...') : t('comment-reply', 'Reply')}
                    </button>
                </form>
            )}

            {!isReply && replyCount > 0 && (
                <button className="comment-toggle-replies" onClick={toggleReplies}>
                    {showReplies
                        ? t('comment-hide-replies', 'Hide replies')
                        : `${t('comment-view', 'View')} ${replyCount} ${replyCount === 1 ? t('comment-reply-singular', 'reply') : t('comment-reply-plural', 'replies')}`}
                </button>
            )}

            {!isReply && showReplies && (
                <div className="comment-replies">
                    {replies.map((r) => (
                        <CommentItem
                            key={r.publicId}
                            comment={r}
                            picturePublicId={picturePublicId}
                            currentUserPublicId={currentUserPublicId}
                            translations={translations}
                            isReply
                            onReplyDeleted={onReplyDeleted}
                            onDeleted={(pid) => {
                                setReplies((prev) => prev.filter((x) => x.publicId !== pid));
                                setReplyCount((c) => Math.max(0, c - 1));
                                if (onReplyDeleted) onReplyDeleted();
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Comments({ picturePublicId, onCountChange, translations }) {
    const { user } = useAuth();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [text, setText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [skip, setSkip] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    const t = (key, fallback) => translations?.[key] || fallback;

    const bumpCount = useCallback((delta) => {
        if (onCountChange) onCountChange(delta);
    }, [onCountChange]);

    useEffect(() => {
        let active = true;
        setLoading(true);
        api.getComments(picturePublicId, 0, PAGE)
            .then((data) => {
                if (!active) return;
                setComments(data);
                setSkip(data.length);
                setHasMore(data.length === PAGE);
            })
            .catch(() => {})
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [picturePublicId]);

    const loadMore = async () => {
        try {
            const data = await api.getComments(picturePublicId, skip, PAGE);
            setComments((prev) => [...prev, ...data]);
            setSkip((s) => s + data.length);
            setHasMore(data.length === PAGE);
        } catch {
            // Ignore
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        const value = text.trim();
        if (!value || submitting) return;
        setSubmitting(true);
        try {
            const created = await api.addComment(picturePublicId, value);
            setComments((prev) => [created, ...prev]);
            setText('');
            bumpCount(1);
        } catch {
            // Ignore
        } finally {
            setSubmitting(false);
        }
    };

    const removeComment = (publicId) => {
        const removed = comments.find((c) => c.publicId === publicId);
        setComments((prev) => prev.filter((c) => c.publicId !== publicId));
        // A removed top-level comment takes its replies with it.
        bumpCount(-(1 + (removed?.replyCount || 0)));
    };

    return (
        <section className="comments-section">
            <h2 className="comments-title">{t('comments-title', 'Comments')}</h2>

            {user ? (
                <form className="comment-new" onSubmit={submit}>
                    <textarea
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder={t('comment-placeholder', 'Add a comment...')}
                        maxLength={1000}
                        rows={3}
                    />
                    <button type="submit" className="comment-btn primary" disabled={submitting || !text.trim()}>
                        {submitting ? t('comment-posting', 'Posting...') : t('comment-post', 'Post')}
                    </button>
                </form>
            ) : (
                <p className="comments-login-hint">{t('comments-login-hint', 'Log in to join the conversation.')}</p>
            )}

            {loading ? (
                <div className="comments-loading"><div className="spinner" /></div>
            ) : comments.length === 0 ? (
                <p className="comments-empty">{t('comments-empty', 'No comments yet. Be the first to comment.')}</p>
            ) : (
                <div className="comments-list">
                    {comments.map((c) => (
                        <CommentItem
                            key={c.publicId}
                            comment={c}
                            picturePublicId={picturePublicId}
                            currentUserPublicId={user?.publicId}
                            translations={translations}
                            onDeleted={removeComment}
                            onReplyAdded={() => bumpCount(1)}
                            onReplyDeleted={() => bumpCount(-1)}
                        />
                    ))}
                    {hasMore && (
                        <button className="comment-btn secondary comments-more" onClick={loadMore}>{t('comments-load-more', 'Load more')}</button>
                    )}
                </div>
            )}
        </section>
    );
}
